import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dateTime, scoreFromEnds, validateEnds,
  type Draw, type Game, type League,
} from '@lcc/shared';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Card, Empty, ErrorNote, Loading, Modal, SelectField, Status, TextField,
  errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';

export function SchedulePage() {
  const { can } = useAuth();
  const [from, setFrom] = useState(new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date(Date.now() + 21 * 864e5).toISOString().slice(0, 10));
  const [leagueId, setLeagueId] = useState('');
  const [scoring, setScoring] = useState<{ draw: Draw; game: Game } | null>(null);

  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.get<{ data: League[] }>('/leagues').then((r) => r.data),
  });

  const draws = useQuery({
    queryKey: ['draws', 'range', from, to, leagueId],
    queryFn: () => api.get<{ data: Draw[] }>(
      `/draws${qs({ from, to, leagueId: leagueId || undefined })}`,
    ).then((r) => r.data),
  });

  const leagueName = (id: string) => leagues.data?.find((l) => l.id === id)?.name ?? 'League';

  const needScores = useMemo(
    () => draws.data?.flatMap((d) => d.games
      .filter((g) => g.status === 'scheduled' && new Date(d.scheduledAt) < new Date())
      .map((g) => ({ draw: d, game: g }))) ?? [],
    [draws.data],
  );

  return (
    <>
      <PageHeader
        title="Draws & scores"
        sub={draws.data ? `${draws.data.length} draws in view` : undefined}
      />

      <div className="content stack">
        <Card>
          <div className="row">
            <div style={{ flex: '1 1 160px' }}>
              <TextField label="From" type="date" value={from} onChange={setFrom} />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <TextField label="To" type="date" value={to} onChange={setTo} />
            </div>
            <div style={{ flex: '2 1 240px' }}>
              <SelectField
                label="League" value={leagueId} onChange={setLeagueId}
                options={[
                  { value: '', label: 'All leagues' },
                  ...(leagues.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                ]}
              />
            </div>
          </div>
        </Card>

        {needScores.length > 0 && (
          <div className="banner banner--warn">
            <strong>{needScores.length} game{needScores.length === 1 ? '' : 's'} played but not scored.</strong>{' '}
            Standings will be wrong until they are entered.
          </div>
        )}

        {draws.isLoading ? <Loading what="Loading draws" />
          : draws.error ? <ErrorNote error={draws.error} />
          : !draws.data?.length
            ? <Card><Empty title="No draws in this range">
                Widen the dates, or generate a schedule from a league page.
              </Empty></Card>
            : draws.data.map((d) => {
              const past = new Date(d.scheduledAt) < new Date();
              return (
                <Card
                  key={d.id}
                  title={
                    <h3 style={{ margin: 0 }}>
                      {leagueName(d.leagueId)} <span className="muted" style={{ fontWeight: 400 }}>
                        · draw {d.drawNumber}</span>
                    </h3>
                  }
                  actions={
                    <span className="row row--tight">
                      <span className="small muted nowrap">{dateTime(d.scheduledAt)}</span>
                      <Status value={d.status} />
                    </span>
                  }
                  flush
                >
                  <div className="table-wrap">
                    <table className="t">
                      <thead>
                        <tr>
                          <th>Sheet</th><th>Home</th><th className="num">Score</th>
                          <th>Away</th><th>Status</th><th />
                        </tr>
                      </thead>
                      <tbody>
                        {d.games.map((g) => {
                          const done = g.status === 'final' || g.status === 'forfeit';
                          return (
                            <tr key={g.id}>
                              <td className="mono">{g.sheetNumber ?? '—'}</td>
                              <td className={done && g.winnerTeamId === g.homeTeamId ? '' : undefined}>
                                <b style={{ fontWeight: g.winnerTeamId === g.homeTeamId ? 700 : 400 }}>
                                  {g.homeTeamName ?? <span className="muted">bye</span>}
                                </b>
                              </td>
                              <td className="num nowrap">
                                {g.homeScore !== null && g.awayScore !== null
                                  ? <b>{g.homeScore} – {g.awayScore}</b>
                                  : <span className="muted">–</span>}
                              </td>
                              <td>
                                <b style={{ fontWeight: g.winnerTeamId === g.awayTeamId ? 700 : 400 }}>
                                  {g.awayTeamName ?? <span className="muted">bye</span>}
                                </b>
                              </td>
                              <td><Status value={g.status} /></td>
                              <td className="right">
                                {g.homeTeamId && g.awayTeamId && (
                                  <button
                                    className={done ? 'btn btn--sm' : 'btn btn--sm btn--accent'}
                                    type="button"
                                    onClick={() => setScoring({ draw: d, game: g })}
                                  >
                                    {done ? 'Edit score' : past ? 'Enter score' : 'Score'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              );
            })}
      </div>

      {scoring && can('convenor') !== undefined && (
        <ScoreModal
          draw={scoring.draw}
          game={scoring.game}
          leagueName={leagueName(scoring.draw.leagueId)}
          onClose={() => setScoring(null)}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------ score entry */

/**
 * A line score, entered the way a scoreboard reads: one column per end, one
 * row per team. The running total is computed as you type and the API
 * recomputes it again on save, so the two can never disagree.
 */
function ScoreModal(
  { draw, game, leagueName, onClose }:
  { draw: Draw; game: Game; leagueName: string; onClose: () => void },
) {
  const qc = useQueryClient();
  const toast = useToast();
  const endCount = Math.max(game.endsPlanned, game.ends.length);

  const [rows, setRows] = useState<{ home: string[]; away: string[] }>(() => {
    const home = Array.from({ length: endCount }, () => '');
    const away = Array.from({ length: endCount }, () => '');
    for (const e of game.ends) {
      const i = e.endNumber - 1;
      if (i < 0 || i >= endCount) continue;
      home[i] = e.homePoints ? String(e.homePoints) : e.awayPoints ? '0' : '0';
      away[i] = e.awayPoints ? String(e.awayPoints) : e.homePoints ? '0' : '0';
    }
    return { home, away };
  });
  const [extraEnd, setExtraEnd] = useState(false);
  const [forfeit, setForfeit] = useState<'' | 'home' | 'away'>('');

  const ends = useMemo(() => {
    const out: Array<{ endNumber: number; homePoints: number; awayPoints: number }> = [];
    const n = extraEnd ? rows.home.length : endCount;
    for (let i = 0; i < n; i++) {
      const h = rows.home[i] ?? '';
      const a = rows.away[i] ?? '';
      if (h === '' && a === '') continue;
      out.push({ endNumber: i + 1, homePoints: Number(h || 0), awayPoints: Number(a || 0) });
    }
    return out;
  }, [rows, endCount, extraEnd]);

  const totals = scoreFromEnds(ends);
  const problems = validateEnds(ends);

  const save = useMutation({
    mutationFn: () => api.put<{ data: Game }>(`/games/${game.id}/score`,
      forfeit
        ? {
            status: 'forfeit',
            forfeitTeamId: forfeit === 'home' ? game.homeTeamId : game.awayTeamId,
            ends: [],
            homeScore: null, awayScore: null,
          }
        : { status: 'final', ends, homeScore: null, awayScore: null },
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['draws'] });
      void qc.invalidateQueries({ queryKey: ['standings'] });
      toast.success('Score saved.');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const reopen = useMutation({
    mutationFn: () => api.post(`/games/${game.id}/reopen`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['draws'] });
      void qc.invalidateQueries({ queryKey: ['standings'] });
      toast.show('Score cleared.');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const setCell = (side: 'home' | 'away', i: number) => (raw: string) => {
    const v = raw.replace(/[^0-9]/g, '').slice(0, 1);
    setRows((r) => {
      const next = { home: [...r.home], away: [...r.away] };
      next[side][i] = v;
      // An end belongs to one team: entering a score for one side zeroes the other.
      if (v && v !== '0') next[side === 'home' ? 'away' : 'home'][i] = '0';
      return next;
    });
  };

  const addExtraEnd = () => {
    setRows((r) => ({ home: [...r.home, ''], away: [...r.away, ''] }));
    setExtraEnd(true);
  };

  const columns = rows.home.length;
  const tied = !forfeit && ends.length > 0 && totals.home === totals.away;

  return (
    <Modal
      title={`${leagueName} — sheet ${game.sheetNumber ?? '?'}`}
      wide
      onClose={onClose}
      footer={
        <>
          {(game.status === 'final' || game.status === 'forfeit') && (
            <button className="btn btn--danger" type="button"
              disabled={reopen.isPending} onClick={() => reopen.mutate()}>
              Clear score
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--accent" type="button"
            disabled={save.isPending || problems.length > 0 || (!forfeit && ends.length === 0) || tied}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save score'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>
          {dateTime(draw.scheduledAt)} · draw {draw.drawNumber} · {game.endsPlanned} ends
        </p>

        {save.error ? <ErrorNote error={save.error} /> : null}

        {!forfeit && (
          <div className="sheet-grid">
            <table className="sheet">
              <thead>
                <tr>
                  <th className="team">Team</th>
                  {Array.from({ length: columns }, (_, i) => (
                    <th key={i}>{i + 1 > endCount ? 'X' : i + 1}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {(['home', 'away'] as const).map((side) => (
                  <tr key={side}>
                    <th className="team">
                      {side === 'home' ? game.homeTeamName : game.awayTeamName}
                    </th>
                    {Array.from({ length: columns }, (_, i) => (
                      <td key={i}>
                        <input
                          type="number" min={0} max={8} inputMode="numeric"
                          value={rows[side][i] ?? ''}
                          onChange={(e) => setCell(side, i)(e.target.value)}
                          aria-label={`${side} team, end ${i + 1}`}
                        />
                      </td>
                    ))}
                    <td className="total">{side === 'home' ? totals.home : totals.away}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {problems.length > 0 && (
          <div className="banner banner--error">
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        )}

        {tied && (
          <div className="banner banner--warn">
            The game is tied {totals.home}–{totals.away}. Add an extra end to settle it.
          </div>
        )}

        {!forfeit && (
          <div className="row">
            <button className="btn btn--sm" type="button" onClick={addExtraEnd}>
              Add an extra end
            </button>
            <span className="small muted">
              Leave a cell blank for an end that was not played; enter 0 for a blank end.
            </span>
          </div>
        )}

        <details>
          <summary className="small muted" style={{ cursor: 'pointer' }}>
            Record a forfeit instead
          </summary>
          <div style={{ paddingTop: '.7rem' }}>
            <SelectField
              label="Team that forfeited" value={forfeit}
              onChange={setForfeit}
              options={[
                { value: '' as const, label: 'Neither — the game was played' },
                { value: 'home' as const, label: game.homeTeamName ?? 'Home' },
                { value: 'away' as const, label: game.awayTeamName ?? 'Away' },
              ]}
              hint="The other team is credited with the win and no line score is recorded."
            />
          </div>
        </details>
      </div>
    </Modal>
  );
}
